import Spin from 'antd/lib/spin';
import Radio from 'antd/lib/radio';
import Select from 'antd/lib/select';
import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import withStyles from 'react-jss';
import { fetchQuotes } from 'src/api/shipmentExecution';
import { StylesProps } from 'src/Library/Types';
import {
  getVendorTypeFromQuoteField,
  groupQuotesByContainerSize,
  ProcurementType,
  ProcurementTypeLabels,
  VendorType,
} from '../../../SE.constants';
import { QuoteType, RateSourceType, RfqPlanOption, SLQuoteGroupType } from '../../../SE.interfaces';
import SEQuotes from './SEQuotes';
import SEShippingLineSizeTabs from './SEShippingLineSizeTabs';
import { SEQuoteCandidatesContext, SEQuoteSelectionContext } from './SEQuoteSelectionContext';
import { SEQuotePanelStyles } from './SEQuotePanel.styles';

const styles = () => ({ ...SEQuotePanelStyles() });

interface SEQuotePanelProps extends StylesProps<ReturnType<typeof styles>> {
  field?: any;
}

/**
 * Renders quote selection for ONE vendor type (one RFQ_QUOTE field). Fetches that vendor's quotes,
 * persists the chosen quote into its own field, and reports the selection to the task-level footer.
 */
const SEQuotePanel = (props: SEQuotePanelProps): React.ReactElement => {
  const { classes, field } = props;
  const { shipmentExecutionId, sourceObjectId, name: fieldName, fieldId } = field?.extraData || {};
  const seId = shipmentExecutionId || sourceObjectId;
  const vendorType = getVendorTypeFromQuoteField(field?.extraData);
  // Vendor-type-first so the task-level tab strip can match this selection to its tab
  // (SEQuoteTaskTabs derives the same key from tabMeta via getVendorTypeFromQuoteField).
  const fieldKey = vendorType || fieldName || fieldId || '';

  const { reportSelection } = useContext(SEQuoteSelectionContext);
  const { reportCandidates, savedCandidates } = useContext(SEQuoteCandidatesContext);

  const isShippingLine = vendorType === VendorType.SHIPPING_LINE;

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [quotes, setQuotes] = useState<QuoteType[]>([]);
  // Per-container-size groups returned only for Shipping Line (SMF430); empty for every other vendor.
  const [slGroups, setSlGroups] = useState<SLQuoteGroupType[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [warnings, setWarnings] = useState<string[]>([]);
  // ISS-292834 — vendor's Select-Vendor-Types sub-fields (rank bar) + shipment-level match factors (shown once).
  const [vendorSelectionFields, setVendorSelectionFields] = useState<{ label: string; value: string }[]>([]);
  const [shipmentMatchFactors, setShipmentMatchFactors] = useState<{ label: string; value: string }[]>([]);
  const [selectedQuote, setSelectedQuote] = useState<QuoteType | undefined>(undefined);
  const [isInitialAssignment, setIsInitialAssignment] = useState<boolean>(true);
  // ISS-293278 — the backend-resolved rate source for this tab (drives the Spot/Tender toggle). Undefined
  // until the first fetch resolves, and left untouched on a failed re-fetch so the toggle stays usable.
  const [rateSource, setRateSource] = useState<RateSourceType | undefined>(undefined);
  // ISS-293278 — bumped on every rate-source switch. Shipping Line keeps its per-size picks in its own child
  // state (not in `selectedQuote`), so the panel signals it to drop them via this version rather than being
  // able to clear them directly.
  const [rateBookVersion, setRateBookVersion] = useState<number>(0);
  // ISS-293278 — RFQ picker options for Break Bulk / Inter Carting / Surveyor (control === 'RFQ_PICKER').
  const [rfqOptions, setRfqOptions] = useState<RfqPlanOption[]>([]);

  // `overrides` carry the operator's pick — `rateType` (Spot/Tender toggle) or `rfqPlanId` (RFQ picker);
  // both omitted on the initial fetch so the backend returns its default and echoes it back on `rateSource`.
  const setData = async (overrides: { rateType?: ProcurementType; rfqPlanId?: string } = {}) => {
    if (!seId || !vendorType) return;
    setIsFetching(true);
    const response = await fetchQuotes({ id: seId, vendorType, rateType: overrides.rateType, rfqPlanId: overrides.rfqPlanId });
    if (response.isSuccess) {
      setQuotes(response.data?.quotes || []);
      setSlGroups(response.data?.groups || []);
      setWarnings(response.data?.warnings || []);
      setVendorSelectionFields(response.data?.vendorSelectionFields || []);
      setShipmentMatchFactors(response.data?.shipmentMatchFactors || []);
      if (response.data?.rateSource) setRateSource(response.data.rateSource);
      setRfqOptions(response.data?.rfqOptions || []);
      setErrorMessage('');
    } else {
      setQuotes([]);
      setSlGroups([]);
      setWarnings([]);
      setVendorSelectionFields([]);
      setShipmentMatchFactors([]);
      setErrorMessage(response.errorMessage || 'Unable to fetch quotes.');
    }
    setIsFetching(false);
  };

  useEffect(() => {
    if (field?.disabled) return;
    setData();
  }, [seId, vendorType, field?.disabled]);

  // Snapshot this vendor type's full displayed list to the task modal, which writes it (merged across
  // all included vendor types) into Quote Candidates (SMF427) on submit. Skip the read-only (approval)
  // view — there we render from the saved snapshot, we don't re-capture.
  useEffect(() => {
    if (field?.disabled) return;
    reportCandidates(fieldKey, quotes);
  }, [quotes, fieldKey, field?.disabled]);

  // Hydrate the previously-saved selection (single RankedQuote stored on this field). Shipping Line owns
  // its own array hydration inside SEShippingLineSizeTabs, so skip the single path there.
  useEffect(() => {
    if (isShippingLine) return;
    if (field?.value && isInitialAssignment) {
      setSelectedQuote(field.value);
      setIsInitialAssignment(false);
    }
  }, [field?.value, isShippingLine]);

  // Persist to the field + report to the shared footer whenever the selection changes. Shipping Line
  // reports its per-size array from SEShippingLineSizeTabs instead of this single value.
  useEffect(() => {
    if (isShippingLine) return;
    if (field) {
      field.setValue(selectedQuote);
    }
    reportSelection(fieldKey, selectedQuote);
  }, [selectedQuote, isShippingLine]);

  const onQuoteSelection = (quote: QuoteType) => setSelectedQuote(quote);
  const resetQuote = () => setSelectedQuote(undefined);

  // ISS-293278 — switching the rate source re-fetches this vendor's cards under the new book. The prior pick
  // belongs to the old book, so clear it. `setSelectedQuote(undefined)` covers the single-pick vendors;
  // bumping `rateBookVersion` tells the Shipping Line child (which holds its own per-size picks) to drop
  // them too — otherwise it would re-report a stale selection that isn't in the refreshed candidates and
  // fail submit-time integrity validation.
  const onRateTypeChange = (rateType: ProcurementType) => {
    setSelectedQuote(undefined);
    setRateBookVersion((version) => version + 1);
    setData({ rateType });
  };
  // ISS-293278 — Break Bulk / Inter Carting / Surveyor: switching the source RFQ re-fetches that plan's cards.
  // The prior pick belongs to the old plan, so clear it before re-fetching.
  const onRfqPlanChange = (rfqPlanId: string) => {
    setSelectedQuote(undefined);
    setData({ rfqPlanId });
  };
  // Only show a control when the backend says this vendor/shipment is overridable (Spot shipments) and we're
  // not on the read-only approval view. The control TYPE decides which one renders.
  const isOverridable = !field?.disabled && !!rateSource?.overridable;
  const showRateToggle = isOverridable && rateSource?.control === 'TENDER_TYPE_TOGGLE';
  const showRfqPicker = isOverridable && rateSource?.control === 'RFQ_PICKER';
  // Guard against a non-string rfqPlanId (the backend serialises a null id as `{}`): the Select needs a
  // string value or `undefined`, never an object.
  const rfqPickerValue = typeof rateSource?.rfqPlanId === 'string' ? rateSource.rfqPlanId : undefined;

  if (isFetching) {
    return <div className={classes.loader}><Spin /></div>;
  }

  // Read-only (approval, SMT72) view: show ALL quotes that were available at selection time — this
  // vendor type's slice of the frozen Quote Candidates snapshot (SMF427) — with the chosen quote
  // highlighted. Fall back to the saved selection for snapshots taken before SMF427 existed (Shipping
  // Line stores an array of per-size picks, so flatten it).
  const savedForVendor = savedCandidates?.filter((quote) => quote?.vendorType === vendorType) || [];
  const savedSelection: QuoteType[] = Array.isArray(field?.value)
    ? field.value
    : field?.value
      ? [field.value]
      : [];
  const readOnlyQuotes = savedForVendor.length ? savedForVendor : savedSelection;
  const quoteList = field?.disabled ? readOnlyQuotes : quotes;

  // ISS-292834 — the read-only / approval view never calls quote-search, so the vendor sub-fields + shipment
  // match factors don't come from the live response there. The BE attaches them onto the task-details field
  // instead (getTaskDetailsForShipmentV2), so read them off extraData when disabled.
  const effectiveVendorSelectionFields = field?.disabled
    ? ((field?.extraData?.vendorSelectionFields as { label: string; value: string }[]) || [])
    : vendorSelectionFields;
  const effectiveShipmentMatchFactors = field?.disabled
    ? ((field?.extraData?.shipmentMatchFactors as { label: string; value: string }[]) || [])
    : shipmentMatchFactors;

  // Shipping Line (SMF430): render the per-container-size split. Prefer the backend groups; when absent
  // (older backend / no container-size data) fall back to grouping the flat list — a single group then
  // renders as one tab, matching legacy single-list behaviour without dropping the array persistence.
  const renderShippingLine = (): React.ReactElement => {
    const groups = field?.disabled
      ? groupQuotesByContainerSize(readOnlyQuotes)
      : (slGroups.length ? slGroups : groupQuotesByContainerSize(quotes));
    return (
      <SEShippingLineSizeTabs
        groups={groups}
        field={field}
        fieldKey={fieldKey}
        reportSelection={reportSelection}
        disabled={field?.disabled}
        rateBookVersion={rateBookVersion}
        vendorSelectionFields={vendorSelectionFields}
        shipmentMatchFactors={shipmentMatchFactors}
      />
    );
  };

  return (
    <div>
      {/* Rate-source control pinned to the TOP of the tab so it stays put regardless of the quote/empty state. */}
      {showRateToggle && (
        <div className={classes.rateToggle}>
          <span className={classes.rateToggleLabel}>Rate type</span>
          <Radio.Group size="small" value={rateSource?.procurementType} onChange={(e) => onRateTypeChange(e.target.value)}>
            <Radio.Button value={ProcurementType.SPOT}>{ProcurementTypeLabels[ProcurementType.SPOT]}</Radio.Button>
            <Radio.Button value={ProcurementType.CONTRACT}>{ProcurementTypeLabels[ProcurementType.CONTRACT]}</Radio.Button>
          </Radio.Group>
        </div>
      )}
      {/* RFQ picker for Break Bulk / Inter Carting / Surveyor — pick which RFQ plan the rates come from. */}
      {showRfqPicker && (
        <div className={classes.rateToggle}>
          <span className={classes.rateToggleLabel}>RFQ</span>
          <Select
            size="small"
            className={classes.rfqSelect}
            value={rfqPickerValue}
            placeholder="Select an RFQ"
            onChange={(value) => onRfqPlanChange(value as string)}
            notFoundContent="No RFQ available for this vendor + lane"
          >
            {rfqOptions.map((option) => (
              <Select.Option key={option.rfqPlanId} value={option.rfqPlanId}>{option.name}</Select.Option>
            ))}
          </Select>
        </div>
      )}
      {errorMessage && <div className={classes.errorText}>{errorMessage}</div>}
      {/* Non-blocking advisories from the ranking pass (e.g. the BB RFQ-vs-execution CBM mismatch).
          Only meaningful for the live search — the read-only approval view renders the frozen snapshot. */}
      {!field?.disabled && warnings.map((warning) => (
        <div key={warning} className={classes.warningText}>{warning}</div>
      ))}
      {isShippingLine ? renderShippingLine() : (
        <SEQuotes
          selectedQuote={selectedQuote}
          quoteList={quoteList}
          onQuoteSelection={onQuoteSelection}
          resetQuote={resetQuote}
          disabled={field?.disabled}
          vendorSelectionFields={effectiveVendorSelectionFields}
          shipmentMatchFactors={effectiveShipmentMatchFactors}
        />
      )}
    </div>
  );
};

export default withStyles(styles)(SEQuotePanel);
